import { NEG_STATUS_FR } from "./negotiationShared";

import { translateFr as tfr } from "@/i18n";
const RESOLUTION_EVENT_FR = {
  confirm_match: tfr("Écart confirmé conforme"), accept_change: tfr("Modification finale acceptée"),
  request_correction: tfr("Correction demandée"), needs_clarification: tfr("À clarifier"), reopen: tfr("Résolution réouverte"),
};

const quoted = (label) => { const m = /«\s*(.+?)\s*»/.exec(label || ""); return m ? m[1] : null; };
const vnum = (e) => e.meta?.version_number ?? (/(?:V|\(V)(\d+)/.exec(e.label || "") || [])[1];

// Renders a localized label for a negotiation timeline event from its structured
// type + metadata (falls back to tf(label) so history is never corrupted).
export const ALERT_TITLE_FR = {
  D7: tfr("expire dans 7 jours"), D3: tfr("expire dans 3 jours"), D1: tfr("expire demain"), EXPIRED: tfr("est arrivée à échéance"),
};

export function offerAlertTitle(a, tf) {
  const who = a.athlete || a.counterparty || "—";
  const prefix = a.alert_type === "EXPIRED" ? `${tf("Offre expirée")} · ` : a.alert_type === "D1" ? `${tf("Action requise")} · ` : "";
  return `${prefix}${tf("La contre-offre pour")} ${who} ${tf(ALERT_TITLE_FR[a.alert_type] || "")}`;
}

export function eventLabel(e, tf) {
  const m = e.meta || {};
  switch (e.type) {
    case "neg_opened":
      return tf("Négociation ouverte");
    case "neg_status_changed":
      return `${tf("Statut de négociation")} : ${tf(NEG_STATUS_FR[m.status] || m.status || "")}`;
    case "neg_term_status":
      return tf("Statut d'élément mis à jour");
    case "neg_version_finalized": {
      const vt = m.version_type || (/^Contre-offre/.test(e.label || "") ? "counter_offer" : null);
      if (vt === "counter_offer") return `${tf("Contre-offre")} V${vnum(e) || ""} ${tf("finalisée")}`;
      if (vt === "offer") return tf("Offre initiale reçue");
      if (vt === "revised_offer") return tf("Nouvelle offre reçue");
      if (vt === "final_agreement") return tf("Accord final enregistré");
      return tf(e.label);
    }
    case "neg_verbal_agreement":
      return tf("Accord verbal enregistré");
    case "neg_final_review":
      return tf("Revue finale complétée — prêt à signer");
    case "neg_signed":
      return tf("Contrat signé (négociation)");
    case "contract_activated":
      return `${tf("Contrat activé depuis la négociation")}${vnum(e) ? ` (V${vnum(e)})` : ""}`;
    case "neg_document_added": {
      const f = m.filename || quoted(e.label);
      return `${tf("Document")} « ${f || "—"} » ${tf("joint à")} V${vnum(e) || "—"}`;
    }
    case "neg_document_removed":
      return `${tf("Document")} « ${m.filename || quoted(e.label) || "—"} » ${tf("retiré")}`;
    case "neg_document_extracted": {
      const f = m.filename || quoted(e.label);
      const n = m.detected ?? (/—\s*(\d+)/.exec(e.label || "") || [])[1];
      return `${tf("Document analysé")} — ${n ?? "—"} ${tf("termes extraits de")} « ${f || "—"} »`;
    }
    case "neg_extraction_ignored":
      return `${tf("Suggestion ignorée")} : ${tf(m.term_label || (e.label || "").split(" : ").slice(1).join(" : "))}`;
    case "neg_extraction_accepted": {
      const lbl = tf(m.term_label || (e.label || "").split(" : ").slice(1).join(" : "));
      return m.action === "modified_and_accepted"
        ? `${tf("Valeur corrigée puis validée")} : ${lbl}`
        : `${tf("Valeur validée")} : ${lbl}`;
    }
    case "neg_reconciliation_run": {
      if (m.match === undefined) return tf(e.label);
      return `${tf("Réconciliation finale")} : ${m.match} ${tf("conformes")}, ${m.difference} ${tf("écarts")}, ${m.missing} ${tf("manquants")}, ${m.new_unexpected} ${tf("inattendus")}`;
    }
    case "neg_reconciliation_resolved": {
      const act = tf(RESOLUTION_EVENT_FR[m.action] || "");
      const lbl = tf(m.term_label || (e.label || "").split(" : ").slice(1).join(" : "));
      return act ? `${act} : ${lbl}` : tf(e.label);
    }
    case "neg_brief_generated":
      return m.kind === "meeting_prep" ? tf("Préparation de réunion générée") : tf("Synthèse de négociation générée");
    case "neg_export_pdf": {
      if (m.kind === "counter_offer") return `${tf("Contre-offre")} V${vnum(e) || ""} ${tf("exportée en PDF")}`;
      if (m.kind === "brief_meeting_prep") return tf("Préparation de réunion exportée en PDF");
      if (m.kind === "brief_summary") return tf("Synthèse de négociation exportée en PDF");
      return tf(e.label);
    }
    case "neg_deadline_alert": {
      const who = m.athlete || "—";
      const map = { D7: tfr("expire dans 7 jours"), D3: tfr("expire dans 3 jours"), D1: tfr("expire demain") };
      return `${tf("Échéance")} : ${tf("la contre-offre pour")} ${who} ${tf(map[m.alert_type] || "")}`;
    }
    case "neg_offer_expired":
      return `${tf("Offre expirée")} · ${tf("La contre-offre pour")} ${m.athlete || "—"} ${tf("est arrivée à échéance")}`;
    case "neg_email_sent":
      return `${tf("Contre-offre")} V${vnum(e) || ""} ${tf("envoyée à")} ${m.counterparty || m.recipient || "—"}`;
    case "neg_email_failed":
      return `${tf("Échec d'envoi de la contre-offre")} V${vnum(e) || ""} (${m.recipient || "—"})`;
    default:
      return tf(e.label);
  }
}

export async function downloadPdf(api, url, body, filename) {
  const r = await api.post(url, body, { responseType: "blob" });
  const blob = new Blob([r.data], { type: "application/pdf" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(a.href);
}
